// @flow
import React, { Component, Fragment } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import queryString from 'query-string';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import FilterDrawer from '../../../../elements/Filter/Drawer/FilterDrawer';
import Filter from '../../../../elements/Filter/Filter';
import update from 'immutability-helper';
import InfiniteScroll from '../../../../elements/InfiniteScroll/InfiniteScroll';
import AlertContainer from '../../../alert/container/AlertContainer';
import { generateTags } from '../../../../elements/Filter/utils';
// import InfiniteScroll from '../../../../elements/InfiniteScroll/InfiniteScroll';
import { urlVerifier } from '../../../../utils/string';
import { getLocalizedCountry } from '../../../local-values/utils/countries';
import Loading from '../../../../elements/Loading/Loading';
import ResultBox from './ResultBox/ResultBox';
import environment from '../../../../environment';

import style from './SearchStep.scss';
import icons from '../../../../assets/styles/icons.scss';
import inputStyle from '../../../../assets/styles/input.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import filterStyles from '../../../../assets/styles/filter.scss';
import filterOptionsStyle from '../../../../assets/styles/filter-options.scss';
import amazonLogo from '../../../../assets/img/new-order/search-step/amazon.png';

import type { Location as RouterLocation } from '../../../../router';
import type { FilterCheckbox } from '../../../../elements/Filter/Filter';
import type { OnlineStore } from '../../../../entities';
import type { LocalValuesState } from '../../../local-values/duck';
import type { HTTPError } from '../../../../error';
import type { SearchProduct } from '../duck';
import type { LocaleState } from '../../../locale/duck';

export type Props = {
  +locale: LocaleState;
  +location: RouterLocation;
  +localValues: LocalValuesState;
  +searchProduct: (name: string, queryFilter?: { [key: string]: string }, resetPagination?: boolean) => void;
  +isFetchingProducts?: boolean;
  +products: SearchProduct[];
  +fetchProductsError: HTTPError;
  +localeState: LocaleState;
  +goToRedirect: (route: string) => void;
  +pageCanAsk?: boolean;
  +changeQuery: (querySearch: string) => void;
};

export type State = {
  name: {
    value: string;
    pristine: boolean;
  };
  drawerOpen: boolean;
  filter: {
    minPrice: {
      value: string;
    };
    maxPrice: {
      value: string;
    };
    openPrice: boolean;
    stores: {
      [key: string]: boolean;
    };
    openStores: boolean;
  };
  sort: {
    value: string;
    name: string;
  };
  order: {
    value: 'ASC' | 'DESC';
  };
};

const messages = defineMessages({
  sortHighestTotalValue: {
    id: 'sort.option.highestTotalValue',
  },
  sortLowestTotalValue: {
    id: 'sort.option.lowestTotalValue',
  },
  title: {
    id: 'newOrder.search.title',
  },
});

const initialState = {
  name: {
    value: '',
    pristine: true,
  },
  drawerOpen: false,
  filter: {
    minPrice: {
      value: '0',
    },
    maxPrice: {
      value: '5000',
    },
    openPrice: true,
    stores: {},
    openStores: false,
  },
  sort: {
    value: '',
    name: '',
  },
  order: {
    value: 'DESC',
  },
};

export default class SearchStep extends Component<Props, State> {
  state = initialState;
  changeNumber$ = Subject;
  changeNumberSubscription = Subscription;
  lastName: string;
  
  constructor(props: Props) {
    super(props);
    const { location } = props;
    const parsedQuery = queryString.parse(location.search);
    // this.changeNumber$ = new Subject();
    this.state = {
      ...this.state,
      name: {
        value: parsedQuery.name || '',
        pristine: true,
      },
    };
  }

  componentDidMount() {
    const { searchProduct } = this.props;
    const { name } = this.state;
    if (!!name.value) {
      searchProduct(name.value, this.buildQueryParams(), true);
    }
    
    /*this.changeNumberSubscription = this.changeNumber$
      .pipe(debounceTime(1000))
      .subscribe(() => {
        searchProduct(this.state.name.value, this.buildQueryParams(), true);
      });*/
    
    window.addEventListener('message', this.handleReceiveMessage);
  }
  
  componentDidUpdate(prevProps: Props) {
    const { isFetchingProducts, fetchProductsError } = this.props;
    const { name } = this.state;
    if (!!prevProps.isFetchingProducts && !isFetchingProducts && !fetchProductsError) {
      this.lastName = name.value;
    }
  }
  
  /*componentWillUnmount() {
    if (this.changeNumber$) {
      this.changeNumber$.unsubscribe();
    }
    if (this.changeNumberSubscription) {
      this.changeNumberSubscription.unsubscribe();
    }
  }*/

  componentWillUnmount() {
    window.removeEventListener('message', this.handleReceiveMessage);
  }

  handleChange = (e: Event) => {
    const { searchProduct } = this.props;
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const pristine = this.props[name] === value;
      this.setState({ 
        [name]: { value, pristine }, 
      });
    }
  };

  handleSearch = (e?: Event) => {
    if (e) e.preventDefault();
    const { searchProduct, goToRedirect, changeQuery, location } = this.props;
    const { name } = this.state;
    if (name.value !== '') {
      searchProduct(name.value, this.buildQueryParams());
    }

    if (urlVerifier(name.value) || name.value.startsWith('http')) {
      goToRedirect(`/new-order/product?initialQuery=${name.value}`);
    } else {
      // searchProduct(name.value, this.buildQueryParams(), this.lastName !== name.value);
      searchProduct(name.value, this.buildQueryParams(), true);
      const parsedQuery = queryString.parse(location.search);
      parsedQuery.name = name.value;
      changeQuery(queryString.stringify(parsedQuery));
    }
  };

  handleChangeSort = (e: Event) => {
    if (e.target instanceof HTMLSelectElement) {
      const option = e.target.options[e.target.selectedIndex];
      const dataset: { sort: string, order: 'ASC' | 'DESC' } = (option.dataset: any);
      // this.fireEvent();
      this.setState({
        sort: {
          value: option.value,
          name: dataset.sort,
        },
        order: {
          value: dataset.order,
        },
      }, () => this.handleSearch());
    }
  };

  buildQueryParams(): { [key: string]: any } {
    const params = {};
    const { filter, sort, order } = this.state;
    /*const stores = Object.keys(filter.stores)
      .reduce((acum, act: string) => filter.stores[act] ? acum.concat(act) : acum, []);
    if (filter.minPrice) params.minprice = filter.minPrice.value;
    if (filter.maxPrice) params.maxprice = filter.maxPrice.value;
    if (stores.length) {
      params.storeIds = stores.join(',');
    }*/

    // Sort & Ord
    /*if (sort.name) {
      params.sort = sort.name;
    }
    if (order.value) {
      params.order = order.value;
    }*/
    return params;
  }

  handleChangeStore(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('stores', { [value]: !filter.stores[value] });
    }
  }

  // Depends if desktop or mobile
  handleChangeFilterValueCreator = (isDesktop: boolean) =>
    (name: string, value: any) => {
      this.setState((state: State) => {
        const c = update(state.filter[name], { $merge: value });
        return {
          filter: update(state.filter, {
            [name]: { $set: c },
          }),
        };
      }, () => {
        if (isDesktop) this.handleSearch();
      });
    };

  handleChangeFilterSingleValueCreator = (isDesktop: boolean) =>
    (name: string, value: any) => {
      this.setState((state: State) => {
        return {
          filter: update(state.filter, {
            [name]: { $set: value },
          }),
        };
      }, () => {
        if (isDesktop) this.handleSearch();
      });
    };

  handleOpen = (name: string) => {
    this.setState((state: State) => {
      return {
        filter: update(state.filter, {
          [name]: { $set: !state.filter[name] },
        }),
      };
    });
  };

  handleChangePrice = (values: number[], isDesktop: boolean = true) => {
    this.handleChangeFilterSingleValueCreator(isDesktop)('minPrice', { value: values[0] });
    this.handleChangeFilterSingleValueCreator(isDesktop)('maxPrice', { value: values[1] });
  };

  generateFilterFields = (stores: ?FilterCheckbox[], { filter }: State, isDesktop: boolean) => [
    {
      type: 'range',
      titleIntl: 'filter.price.title',
      isOpen: filter.openPrice,
      name: 'price',
      minStateValue: filter.minPrice,
      maxStateValue: filter.maxPrice,
      options: {
        min: 0,
        max: 5000,
        defaultValue: [0, 5000],
        onChange: (values: number[]) => this.handleChangePrice(values, isDesktop),
      },
      handleOpen: () => this.handleOpen('openPrice'),
    },
    // NOTE: Removed temporarily because available just Amazon
    /*{
      type: 'checkbox',
      titleIntl: 'filter.stores.title',
      handler: (e: Event) => this.handleChangeStore(e, isDesktop),
      stateValue: filter.stores,
      name: 'stores',
      checkboxes: stores,
      isOpen: filter.openStores,
      color: 'lightBlue',
      handleOpen: () => this.handleOpen('openStores'),
    },*/
  ];

  handleOpenDrawer = () => {
    this.setState({ drawerOpen: true });
  };

  handleCloseDrawer = () => {
    this.setState({
      drawerOpen: false,
    });
    this.handleSearch()
  };

  handleClearFilters = () => {
    this.setState((prevState: State) => ({ ...initialState, name: prevState.name }),
      () => this.handleSearch());
  };

  handleDeleteTag = (key?: string, name?: string) => {
    const updateQuery = {};
    switch (key) {
      case 'stores':
        if (name) {
          updateQuery[key] = {
            [name]: { $set: false },
          };
        }
        break;
    }
    this.setState((prevState: State) => update(prevState, {
      filter: updateQuery,
    }), () => this.handleSearch());
  };
  
  handleReceiveMessage = (message) => {
    const qsp = document.querySelector('#qempo-search-pages');
    
    if (!qsp || !qsp.contentWindow) return;

    // console.log('[handleReceiveMessage]', message.origin, message)
    
    if (message.origin !== 'https://pages.dev.qempo.io' && message.origin !== 'https://pages.qempo.io') {
      // console.log('[handleReceiveMessage] skip');
      return;
    }
    
    if (!!message.data) {
      console.log('[handleReceiveMessage]', message.data.event, message.data);
      
      if (message.data.event === 'SEARCHING') {
        qsp.style.height = `${message.data.data.scrollHeight}px`;
      } else if (message.data.event === 'FINISHED_SEARCH') {
        qsp.style.height = `${message.data.data.scrollHeight}px`;
      }
    }


    /*if (message.source !== qsp.contentWindow) {
      console.log('[handleReceiveMessage] skip');
      return;
    }*/
  };

  render() {
    const { name, sort, drawerOpen } = this.state;
    /*const { localValues, products, isFetchingProducts, searchProduct, localeState, locale, pageCanAsk } = this.props;
    const stores: ?FilterCheckbox[] = localValues.onlineStores && localValues.onlineStores.map((onlineStore: OnlineStore) => (
      {
        label: onlineStore.name,
        value: onlineStore.onlineStoreId.toString(),
      }
    ));
    const filterFields = this.generateFilterFields(stores, this.state, true);
    const tags: any = generateTags((filterFields: any), this.handleDeleteTag, this.context.intl.formatMessage);
    const country = locale && getLocalizedCountry(locale.domainCountry || 'PE', this.context.intl.formatMessage, localValues.countriesMap);
    const sortNativeSelect = (
      <select value={sort.value}
              onChange={this.handleChangeSort}>
        <option value="highestTotalValue" data-sort="TOTAL"
                data-order="DESC">{this.context.intl.formatMessage(messages.sortHighestTotalValue)}</option>
        <option value="lowestTotalValue" data-sort="TOTAL"
                data-order="ASC">{this.context.intl.formatMessage(messages.sortLowestTotalValue)}</option>
      </select>
    );

    const filterContent =
      <Filter filterFields={(this.generateFilterFields(stores, this.state, true): any)}
              handleDeleteTag={this.handleDeleteTag}
              hasBorder/>;
    const noResult = (!products || !products.length) && !isFetchingProducts;

    const productsResult = products && products.map((product: SearchProduct, n: number) => (
      <ResultBox key={n}
                 locale={localeState}
                 product={product}/>
    ));*/

    return (
      <Fragment>
        <iframe id="qempo-search-pages"
                className={style.newSearch}
                scrolling="no"
                src={`${environment.qempoIoPagesUrl}/product/amazon/search/asindata?keyword=${encodeURIComponent(name.value)}`} 
                frameBorder="0" />
        {/*<IframeResizer
          log
          inPageLinks
          src={`https://pages.dev.qempo.io/product/amazon/search/asindata?keyword=${encodeURIComponent(name)}`}
          width="100%"
          scrolling="no"
        />*/}
        
        {/*<section className={style.searchStepFormWrap}>
          <section className={style.searchStepForm}>
            <img src={amazonLogo} alt="Qempo"/>
            <FormattedMessage id="newOrder.search.title">{
              txt => <h2>{`${txt} ${country}`}</h2>
            }</FormattedMessage>
            <FormattedMessage id="newOrder.search.input.label">{
              text => <p className={style.searchLabel}>{text}</p>
            }</FormattedMessage>
            <form className={style.nameWrap}
                  onSubmit={this.handleSearch}>
              <FormattedMessage id="newOrder.search.name.placeholder">{
                txt => <input type="text"
                              name="name"
                              value={name.value}
                              className={classNames(inputStyle.input, style.name)}
                              onChange={this.handleChange}
                              placeholder={txt}
                              autoComplete="disabled"/>
              }</FormattedMessage>
              <button type="submit"
                      className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.search)}>
                <i className={icons.symbolSearch}/>
                <FormattedMessage id="newOrder.search.button">{
                  txt => <p>{txt}</p>
                }</FormattedMessage>
              </button>
            </form>
          </section>
        </section>*/}
        
        
        
        {/*<section className={filterOptionsStyle.filterOptions}>
          <section className={filterOptionsStyle.header}>
            <FormattedMessage id="stores.filter.title">{
              text => <p className={filterOptionsStyle.title}><i className={icons.symbolTweak}/>{text}</p>
            }</FormattedMessage>
            <section className={filterOptionsStyle.sortOptions}>
              <FormattedMessage id="sort.title">{
                txt => <label>{txt}</label>
              }</FormattedMessage>
              {sortNativeSelect}
            </section>
            <section className={filterOptionsStyle.filterDrawer}>
              <FilterDrawer drawerOpen={drawerOpen}
                            filterFields={(this.generateFilterFields(stores, this.state, false): any)}
                            color="lightBlue"
                            handleCloseDrawer={this.handleCloseDrawer}
                            handleOpenDrawer={this.handleOpenDrawer}
                            handleClearFilters={this.handleClearFilters}/>
            </section>
          </section>
        </section>*/}
        {/*<section className={style.mainWrap}>
          <aside className={style.filterWrap}>
            {filterContent}
          </aside>
          <section className={style.rightWrap}>
            <AlertContainer showId="order"/>
            <section className={style.resultNameWrap}>
              <FormattedMessage id="newOrder.search.resultName">{
                txt => <p className={style.resultName}>{txt} {name.value}</p>
              }</FormattedMessage>
            </section>
            {!!tags.length && <ul className={filterStyles.tags}>
              {tags}
            </ul>}
            {noResult &&
            <FormattedMessage id="newOrder.search.noResults">{
              txt => <p className={style.noResults}>{txt}</p>
            }</FormattedMessage>}
            <InfiniteScroll offset={550}
                            disabled={!pageCanAsk}
                            handleCheckpoint={() => {
                              if (!isFetchingProducts) {
                                searchProduct(name.value, this.buildQueryParams());
                              }
                            }}>
              <section className={style.results}>
                {productsResult}
              </section>
            </InfiniteScroll>
            {isFetchingProducts && <Loading className={style.loading} color="lightBlue"/>}
          </section>
        </section>*/}
      </Fragment>
    );
  }
}

SearchStep.propTypes = {
  locale: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  searchProduct: PropTypes.func,
  isFetchingProducts: PropTypes.bool,
  products: PropTypes.array,
  fetchProductsError: PropTypes.object,
  localeState: PropTypes.object.isRequired,
  goToRedirect: PropTypes.func.isRequired,
  pageCanAsk: PropTypes.bool,
  changeQuery: PropTypes.func.isRequired,
};

SearchStep.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/SearchStep/components/SearchStep.jsx